import { useLocation, useNavigate } from 'react-router-dom'
import {
    Home, Send, ArrowDownLeft, Clock, Settings,
    Wallet, Gem, Zap, HelpCircle, Info
} from 'lucide-react'
import { BottomNav } from './BottomNav'
import { useWallet } from '@/hooks/useWallet'
import { useAuth } from '@/hooks/useAuth'

const navItems = [
    { path: '/', icon: Home, label: 'Home' },
    { path: '/send', icon: Send, label: 'Send' },
    { path: '/receive', icon: ArrowDownLeft, label: 'Receive' },
    { path: '/history', icon: Clock, label: 'Activity' },
    { path: '/settings', icon: Settings, label: 'Settings' },
]

const bottomLinks = [
    { path: '/help', icon: HelpCircle, label: 'Help Center' },
    { path: '/wellswallet', icon: Info, label: 'About Wells' },
]

function DesktopSidebar() {
    const location = useLocation()
    const navigate = useNavigate()
    const { wallet } = useWallet()
    const { profile } = useAuth()

    const mindGems = wallet?.mindGemsBalance ?? 0
    const fluxon = wallet?.fluxonBalance ?? 0

    return (
        <aside className="hidden lg:flex fixed left-0 top-0 bottom-0 w-64 xl:w-72 flex-col bg-[#0d0d14]/95 backdrop-blur-xl border-r border-white/[0.04] z-20">
            {/* Logo */}
            <div className="p-6 border-b border-white/[0.04]">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg shadow-purple-500/15">
                        <Wallet className="h-5 w-5 text-white" />
                    </div>
                    <div>
                        <h1 className="text-white font-bold text-sm">Wells Wallet</h1>
                        <p className="text-white/30 text-xs">Student Finance</p>
                    </div>
                </div>
            </div>

            {/* User Card */}
            {profile && (
                <div className="px-4 py-3 border-b border-white/[0.04]">
                    <div className="flex items-center gap-3 p-2.5 rounded-xl bg-white/[0.03]">
                        <div
                            className="w-9 h-9 rounded-lg flex items-center justify-center text-sm font-bold text-white flex-shrink-0"
                            style={{ background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)' }}
                        >
                            {profile?.first_name?.[0] || '?'}
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-white font-medium text-sm truncate">
                                {profile?.first_name || 'Student'} {profile?.last_name?.[0] ? `${profile.last_name[0]}.` : ''}
                            </p>
                            <p className="text-white/25 text-xs font-mono">#{wallet?.studentTag || '…'}</p>
                        </div>
                    </div>
                </div>
            )}

            {/* Nav */}
            <nav className="flex-1 p-4 space-y-0.5 overflow-y-auto">
                <p className="text-white/25 text-[10px] font-semibold uppercase tracking-widest mb-2 px-3">Menu</p>
                {navItems.map((item) => {
                    const isActive = location.pathname === item.path
                    return (
                        <button
                            key={item.path}
                            onClick={() => navigate(item.path)}
                            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors text-sm ${isActive
                                ? 'bg-white/[0.06] text-white'
                                : 'text-white/40 hover:bg-white/[0.03] hover:text-white/60'
                                }`}
                        >
                            <item.icon className={`h-[18px] w-[18px] ${isActive ? 'text-indigo-400' : ''}`} />
                            <span className="font-medium">{item.label}</span>
                            {isActive && (
                                <div className="ml-auto w-1 h-4 bg-gradient-to-b from-indigo-400 to-purple-500 rounded-full" />
                            )}
                        </button>
                    )
                })}

                <div className="my-3 border-t border-white/[0.03]" />

                <p className="text-white/25 text-[10px] font-semibold uppercase tracking-widest mb-2 px-3">Support</p>
                {bottomLinks.map((item) => {
                    const isActive = location.pathname === item.path
                    return (
                        <button
                            key={item.path}
                            onClick={() => navigate(item.path)}
                            className={`w-full flex items-center gap-3 px-3 py-2 rounded-xl transition-colors text-sm ${isActive
                                ? 'text-white bg-white/[0.04]'
                                : 'text-white/25 hover:bg-white/[0.03] hover:text-white/40'
                                }`}
                        >
                            <item.icon className="h-4 w-4" />
                            <span>{item.label}</span>
                        </button>
                    )
                })}
            </nav>

            {/* Balance Footer */}
            <div className="p-4 border-t border-white/[0.04]">
                <div className="p-3 rounded-xl bg-white/[0.03]">
                    <p className="text-white/25 text-[10px] font-semibold uppercase tracking-wider mb-2">Balances</p>
                    <div className="space-y-2">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <Gem className="h-3.5 w-3.5 text-purple-400" />
                                <span className="text-white/40 text-xs">Mind Gems</span>
                            </div>
                            <span className="text-white font-semibold text-xs tabular-nums">{mindGems.toLocaleString()}</span>
                        </div>
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <Zap className="h-3.5 w-3.5 text-yellow-400" />
                                <span className="text-white/40 text-xs">Fluxon</span>
                            </div>
                            <span className="text-white font-semibold text-xs tabular-nums">{fluxon.toLocaleString()}</span>
                        </div>
                    </div>
                </div>
                <div className="mt-2 px-1">
                    <div className="flex items-center justify-between mb-1">
                        <span className="text-white/20 text-[10px]">Level {wallet?.walletLevel || 1}</span>
                        <span className="text-white/20 text-[10px] tabular-nums">Trust {wallet?.trustScore || 50}%</span>
                    </div>
                    <div className="h-0.5 bg-white/[0.04] rounded-full overflow-hidden">
                        <div
                            className="h-full bg-gradient-to-r from-indigo-500 to-purple-600 rounded-full transition-all duration-700"
                            style={{ width: `${wallet?.trustScore || 50}%` }}
                        />
                    </div>
                </div>
            </div>
        </aside>
    )
}

// Subtle ambient gradient — not animated to avoid GPU drain
function AppBackground() {
    return (
        <div className="fixed inset-0 pointer-events-none overflow-hidden">
            <div className="absolute -top-32 -right-32 w-[400px] h-[400px] lg:w-[500px] lg:h-[500px] bg-purple-500/[0.07] rounded-full blur-[120px]" />
            <div className="absolute top-1/2 -left-24 w-[300px] h-[300px] lg:w-96 lg:h-96 bg-cyan-500/[0.05] rounded-full blur-[100px]" />
            <div className="absolute -bottom-16 right-1/4 w-[280px] h-[280px] bg-indigo-500/[0.04] rounded-full blur-[100px]" />
        </div>
    )
}

interface AppShellProps {
    children: React.ReactNode
    hideBackground?: boolean
}

export function AppShell({ children, hideBackground = false }: AppShellProps) {
    return (
        <div className="min-h-screen bg-[#0a0a0f] relative">
            {!hideBackground && <AppBackground />}

            {/* Desktop Sidebar */}
            <DesktopSidebar />

            {/* Main content — mobile: extra bottom padding for nav + safe area; desktop: sidebar offset */}
            <div className="lg:ml-64 xl:ml-72 relative z-10 pb-24 lg:pb-6">
                {children}
            </div>

            {/* Mobile Tab Bar */}
            <div className="lg:hidden">
                <BottomNav />
            </div>
        </div>
    )
}
